// apps/web/src/features/commercial-proposals/components/CommercialProposalModeControl.tsx
//
// @aiPurpose the 3-way KP composition selector (#1410): BOTH / BUYOUT /
//   LOGISTICS, bound to the editor form's `mode`. Mirrors the editor's
//   PaymentSchemeControl radiogroup pattern. The selected mode gates which
//   field groups the editor renders and which section(s) the calc/projection
//   emit; hidden-section values are retained in form state, never cleared here.
import { useTranslation } from 'react-i18next';
import { COMMERCIAL_PROPOSAL_MODES, type CommercialProposalMode } from '@magimp/shared';

// Static slug + i18n-key map keeps the testid + translation key as string
// LITERALS (i18next types reject a widened `string` key).
const MODE_META: Record<
  CommercialProposalMode,
  { slug: string; labelKey: 'editor.mode.both' | 'editor.mode.buyout' | 'editor.mode.logistics' }
> = {
  BOTH: { slug: 'both', labelKey: 'editor.mode.both' },
  BUYOUT: { slug: 'buyout', labelKey: 'editor.mode.buyout' },
  LOGISTICS: { slug: 'logistics', labelKey: 'editor.mode.logistics' },
};

export function CommercialProposalModeControl({
  value,
  onChange,
}: {
  value: CommercialProposalMode;
  onChange: (v: CommercialProposalMode) => void;
}) {
  const { t } = useTranslation('commercialProposals');
  return (
    <div role="radiogroup" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {COMMERCIAL_PROPOSAL_MODES.map((mode) => {
        const selected = mode === value;
        const meta = MODE_META[mode];
        return (
          <button
            key={mode}
            type="button"
            role="radio"
            aria-checked={selected}
            data-testid={`commercial-proposals-mode-${meta.slug}`}
            onClick={() => onChange(mode)}
            style={{
              flex: '1 1 30%',
              minWidth: 120,
              padding: '8px 10px',
              borderRadius: 'var(--r-2)',
              border: `1px solid ${selected ? 'var(--accent-1)' : 'var(--border-1)'}`,
              background: selected ? 'var(--accent-ring)' : 'var(--bg-1)',
              color: 'var(--fg-1)',
              fontSize: 12,
              fontWeight: selected ? 600 : 400,
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            {t(meta.labelKey)}
          </button>
        );
      })}
    </div>
  );
}
